import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { store } from "../store";
import { Language } from '../types/language';

interface TMDBError extends AxiosError {
  isCache?: boolean;
  response?: AxiosResponse;
  config?: any;
  _retry?: boolean;
}

interface CacheEntry {
  timestamp: number;
  data: any;
}

interface TMDBRequestConfig extends InternalAxiosRequestConfig {
  isCache?: boolean;
}

const TMDB_API_KEY = import.meta.env.VITE_TMDB_API_KEY;
const TMDB_ACCESS_TOKEN = import.meta.env.VITE_TMDB_ACCESS_TOKEN;
const BASE_URL = "https://api.themoviedb.org/3";

// Cache pour les requêtes
const cache = new Map<string, CacheEntry>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000, // 10 secondes timeout
  headers: {
    'Authorization': `Bearer ${TMDB_ACCESS_TOKEN}`,
    'Accept': 'application/json',
  },
  params: {
    api_key: TMDB_API_KEY,
  },
});

// Fonction pour gérer les erreurs
const handleError = (error: TMDBError) => {
  console.error('TMDB API Error:', error.message);
  if (error.response) {
    console.error('Response data:', error.response.data);
    console.error('Response status:', error.response.status);
  }
  throw error;
};

// Fonction pour générer une clé de cache
const getCacheKey = (config: TMDBRequestConfig): string => {
  return `${config.url}?${new URLSearchParams(config.params).toString()}`;
};

// Fonction pour vérifier si une entrée du cache est valide
const isCacheValid = (cacheEntry: CacheEntry): boolean => {
  return Date.now() - cacheEntry.timestamp < CACHE_DURATION;
};

api.interceptors.request.use(
  async (config: TMDBRequestConfig) => {
    // Vérifier le cache
    const cacheKey = getCacheKey(config);
    const cachedData = cache.get(cacheKey);

    if (cachedData && isCacheValid(cachedData)) {
      config.isCache = true;
      throw {
        isCache: true,
        response: {
          data: cachedData.data,
          status: 200,
          statusText: "OK",
          headers: {},
          config,
        },
      };
    }

    const language = store.getState().language.current;
    config.params = {
      ...config.params,
      language: language === Language.FR ? "fr-FR" : "en-US",
      include_image_language: language === Language.FR ? "fr,en" : "en,fr",
    };

    if (!config.headers["Authorization"]) {
      config.headers["Authorization"] = `Bearer ${TMDB_ACCESS_TOKEN}`;
    }

    console.debug('TMDB API Request:', config.url);
    return config;
  },
  (error) => {
    console.error('TMDB API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Mettre en cache la réponse
    const config = response.config as TMDBRequestConfig;
    if (!config.isCache) {
      const cacheKey = getCacheKey(config);
      cache.set(cacheKey, {
        timestamp: Date.now(),
        data: response.data,
      });
    }
    return response;
  },
  (error: TMDBError) => {
    if (error.isCache) {
      return error.response;
    }
    return handleError(error);
  }
);

export const tmdbApi = {
  getTrending: async (
    mediaType: "movie" | "tv" | "all",
    timeWindow: "day" | "week" = "week",
    page: number = 1,
  ) => {
    try {
      const response = await api.get(`/trending/${mediaType}/${timeWindow}`, {
        params: {
          page,
          language: store.getState().language.current || "fr-FR",
        },
      });
      return response.data;
    } catch (error) {
      handleError(error as TMDBError);
      throw error;
    }
  },

  getDiscover: async (
    mediaType: "movie" | "tv",
    page: number = 1,
    sortBy: string = "popularity.desc",
    genreId?: number,
  ) => {
    const today = new Date().toISOString().split('T')[0];
    const isReleaseDateSort = sortBy.includes("release_date") || sortBy.includes("first_air_date");

    const response = await api.get(`/discover/${mediaType}`, {
      params: {
        page,
        sort_by: sortBy,
        with_genres: genreId,
        language: "fr-FR",
        include_adult: false,
        include_video: false,
        // Filtre pour avoir uniquement les films/séries avec suffisamment d'informations
        "vote_count.gte": 20, // Au moins quelques votes
        "with_overview": true, // Avec une description
        "with_poster": true, // Avec une affiche
        ...(isReleaseDateSort && {
          [mediaType === "movie" ? "release_date.lte" : "first_air_date.lte"]: today,
        }),
      },
    });

    // Filtrer les résultats pour ne garder que ceux avec suffisamment d'informations
    const results = response.data.results.filter((item: any) => {
      return (
        item.overview && // A une description
        item.poster_path && // A une affiche
        item.vote_count >= 20 && // A suffisamment de votes
        item.backdrop_path && // A une image de fond
        (mediaType === "movie" ? item.release_date : item.first_air_date) // A une date de sortie
      );
    });

    return {
      ...response.data,
      results,
      total_results: results.length,
    };
  },

  searchMulti: async (query: string, page: number = 1) => {
    const response = await api.get("/search/multi", {
      params: {
        query,
        page,
        language: "fr-FR",
        include_adult: false,
      },
    });

    // Filtrer les résultats - critères plus souples pour la recherche
    const results = response.data.results.filter((item: any) => {
      if (item.media_type === "person") return false; // Exclure les personnes de la barre de recherche
      // Critères minimaux : avoir un poster et un titre/nom
      return (
        item.poster_path &&
        (item.title || item.name) &&
        (item.media_type === "movie" || item.media_type === "tv")
      );
    });

    return {
      ...response.data,
      results,
      total_results: results.length,
    };
  },

  search: async (query: string, page: number = 1) => {
    const response = await api.get("/search/multi", {
      params: {
        query,
        page,
        language: "fr-FR",
        include_adult: false,
      },
    });

    return response.data;
  },

  getDetails: async (mediaType: "movie" | "tv", id: number) => {
    return api
      .get(`/${mediaType}/${id}`, {
        params: {
          append_to_response: 'credits,videos,images,recommendations',
          include_video_language: 'en,fr,de,es,it'
        }
      })
      .then((response) => response.data)
      .catch(handleError);
  },

  getSeasonDetails: async (tvId: number, seasonNumber: number) => {
    const response = await api.get(`/tv/${tvId}/season/${seasonNumber}`, {
      params: {
        append_to_response: "videos,credits",
      },
    });
    return response.data;
  },

  getGenres: async (mediaType: "movie" | "tv") => {
    const response = await api.get(`/genre/${mediaType}/list`, {
      params: {
        language: "fr-FR",
      },
    });
    return response.data;
  },

  getMoviesByGenre: async (genreId: number, page: number = 1) => {
    const response = await api.get("/discover/movie", {
      params: {
        with_genres: genreId,
        page,
        language: "fr-FR",
        include_adult: false,
        include_video: false,
        "vote_count.gte": 20,
        "with_overview": true,
        "with_poster": true,
      },
    });

    const results = response.data.results.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.release_date
      );
    });

    return {
      ...response.data,
      results,
      total_results: results.length,
    };
  },

  getShowsByGenre: async (genreId: number, page: number = 1) => {
    const response = await api.get("/discover/tv", {
      params: {
        with_genres: genreId,
        page,
        language: "fr-FR",
        include_adult: false,
        "vote_count.gte": 20,
        "with_overview": true,
        "with_poster": true,
      },
    });

    const results = response.data.results.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.first_air_date
      );
    });

    return {
      ...response.data,
      results,
      total_results: results.length,
    };
  },

  getPersonDetails: async (personId: number) => {
    const response = await api.get(`/person/${personId}`, {
      params: {
        append_to_response: "combined_credits,images",
      },
    });
    return response.data;
  },

  getPersonMovieCredits: async (personId: number) => {
    const response = await api.get(`/person/${personId}/movie_credits`, {
      params: {
        language: "fr-FR",
      },
    });

    // Filtrer les films dans cast et crew
    const cast = response.data.cast.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.release_date
      );
    });

    const crew = response.data.crew.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.release_date
      );
    });

    return {
      ...response.data,
      cast,
      crew,
    };
  },

  getPersonTVCredits: async (personId: number) => {
    const response = await api.get(`/person/${personId}/tv_credits`, {
      params: {
        language: "fr-FR",
      },
    });

    // Filtrer les séries dans cast et crew
    const cast = response.data.cast.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.first_air_date
      );
    });

    const crew = response.data.crew.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.first_air_date
      );
    });

    return {
      ...response.data,
      cast,
      crew,
    };
  },

  getMovieSuggestions: async (movieId: number) => {
    const response = await api.get(`/movie/${movieId}/recommendations`, {
      params: {
        language: "fr-FR",
      },
    });

    const results = response.data.results.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.release_date
      );
    });

    return {
      ...response.data,
      results,
      total_results: results.length,
    };
  },

  getShowSuggestions: async (showId: number) => {
    const response = await api.get(`/tv/${showId}/recommendations`, {
      params: {
        language: "fr-FR",
      },
    });

    const results = response.data.results.filter((item: any) => {
      return (
        item.overview &&
        item.poster_path &&
        item.vote_count >= 20 &&
        item.backdrop_path &&
        item.first_air_date
      );
    });

    return {
      ...response.data,
      results,
      total_results: results.length,
    };
  },

  getStreamingProviders: async (id: number, type: 'movie' | 'tv') => {
    const response = await api.get(`/${type}/${id}/watch/providers`);
    return response.data;
  }
};
